import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  Timestamp,
  deleteField,
  increment,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Initiate commissioner account deletion with 48-hour grace period.
 * Creates a pendingDeletions doc and marks all commissioner leagues.
 * Returns { leagueIds, executeAt }.
 */
export async function requestAccountDeletion(uid) {
  const leaguesSnap = await getDocs(
    query(collection(db, 'leagues'), where('commissionerId', '==', uid))
  );
  const leagueIds = leaguesSnap.docs.map((d) => d.id);

  const executeAt = new Date(Date.now() + 48 * 60 * 60 * 1000);
  const executeAtTs = Timestamp.fromDate(executeAt);

  await setDoc(doc(db, 'pendingDeletions', uid), {
    uid,
    scheduledAt: Timestamp.now(),
    executeAt: executeAtTs,
    leagueIds,
    status: 'pending',
  });

  if (leagueIds.length) {
    const batch = writeBatch(db);
    for (const leagueId of leagueIds) {
      batch.update(doc(db, 'leagues', leagueId), {
        pendingDeletion: true,
        deletionExecuteAt: executeAtTs,
      });
    }
    await batch.commit();
  }

  return { leagueIds, executeAt };
}

/**
 * Cancel a pending account deletion within the 48-hour window.
 * Removes pendingDeletion flags from all affected leagues.
 */
export async function cancelAccountDeletion(uid) {
  const snap = await getDoc(doc(db, 'pendingDeletions', uid));
  if (!snap.exists() || snap.data().status !== 'pending') return;

  const { leagueIds } = snap.data();
  await updateDoc(doc(db, 'pendingDeletions', uid), { status: 'cancelled' });

  if (leagueIds.length) {
    const batch = writeBatch(db);
    for (const leagueId of leagueIds) {
      batch.update(doc(db, 'leagues', leagueId), {
        pendingDeletion: deleteField(),
        deletionExecuteAt: deleteField(),
      });
    }
    await batch.commit();
  }
}

/**
 * Return the pending deletion record for a user, or null if none.
 */
export async function getPendingDeletion(uid) {
  const snap = await getDoc(doc(db, 'pendingDeletions', uid));
  if (!snap.exists() || snap.data().status !== 'pending') return null;
  return snap.data();
}

/**
 * Immediate account deletion for non-commissioner users.
 * Deletes the Firestore user doc, username entry, and Firebase Auth account.
 * May throw auth/requires-recent-login if the session is stale.
 */
export async function deleteAccountImmediate(firebaseUser) {
  const uid = firebaseUser.uid;

  // Delete leagueMember records and decrement memberCount for each league
  const membersSnap = await getDocs(
    query(collection(db, 'leagueMembers'), where('userId', '==', uid))
  );
  if (!membersSnap.empty) {
    const batch = writeBatch(db);
    membersSnap.docs.forEach((d) => {
      batch.delete(d.ref);
      batch.update(doc(db, 'leagues', d.data().leagueId), { memberCount: increment(-1) });
    });
    await batch.commit();
  }

  // Delete picks
  const picksSnap = await getDocs(
    query(collection(db, 'picks'), where('userId', '==', uid))
  );
  for (let i = 0; i < picksSnap.docs.length; i += 400) {
    const batch = writeBatch(db);
    picksSnap.docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }

  // Delete user doc and username entry
  const userSnap = await getDoc(doc(db, 'users', uid));
  if (userSnap.exists()) {
    const { username } = userSnap.data();
    if (username) await deleteDoc(doc(db, 'usernames', username.toLowerCase()));
  }
  await deleteDoc(doc(db, 'users', uid));
  await firebaseUser.delete();
}
